import React from 'react';

/**
 * Global error boundary — prevents any uncaught render error from producing
 * a blank screen. Shows a recoverable error UI with a reload button.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, info?.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
          <div className="max-w-md w-full bg-white shadow-lg rounded-xl border border-red-100 p-6 text-center" data-testid="error-boundary-fallback">
            <div className="h-12 w-12 mx-auto mb-3 rounded-full bg-red-50 flex items-center justify-center">
              <span className="text-2xl">⚠️</span>
            </div>
            <h1 className="text-lg font-semibold text-slate-900 mb-1">Something went wrong on this screen</h1>
            <p className="text-sm text-slate-500 mb-4">
              The error has been logged. Please reload to continue — your work is safely stored on the server.
            </p>
            <pre className="text-[10px] text-left text-slate-400 bg-slate-50 rounded p-2 mb-4 overflow-x-auto max-h-32">
              {String(this.state.error?.message || this.state.error || 'Unknown error')}
            </pre>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 rounded-lg bg-[#5B6FD6] text-white font-medium hover:bg-[#4a5bc2] transition-colors"
              data-testid="error-boundary-reload"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
